package kz.dentalcrm.app.navigation

import androidx.navigation3.runtime.NavKey

/**
 * Navigation abstraction for DentalCRM app using Navigation 3.
 * 
 * This interface provides high-level navigation operations and supports
 * returning results when navigating back (similar to Activity result API).
 * 
 * Threading: Implementation uses coroutines. Call [bind] once from a composable
 * scope and keep it alive for the lifetime of the UI.
 */
interface AppNavigator {

    /**
     * Pushes [destination] on top of the back stack.
     * 
     * Usage:
     * - Opening a detail screen from a list
     * - Switching tabs by navigating to their root destination
     */
    fun navigate(destination: AppRoute)

    /**
     * Clears the entire back stack and sets [destination] as the only entry.
     * 
     * Usage:
     * - After login, to remove the auth flow and land on the app root
     * - "Reset navigation" scenarios
     */
    fun navigateAndClearBackStack(destination: AppRoute)

    /**
     * Pops back stack entries until [destination] is the top entry.
     * 
     * @param inclusive if true, [destination] itself is also removed
     * 
     * Usage:
     * - Tab re-selection: pop up to the tab root
     * - Returning to a known screen while removing intermediate screens
     */
    fun popUpTo(destination: AppRoute, inclusive: Boolean = false)

    /**
     * Navigates up (pops one entry) and publishes a one-off result.
     * 
     * @param key stable identifier for the result channel/contract
     * @param result the value to publish
     * @param destination optional "pop up to" target before navigating up
     * 
     * Usage:
     * - Detail screen returns updated data to list screen
     * - Form screen returns saved entity to parent screen
     * 
     * Results are delivered through [NavigationResults.results].
     * Consumer should call [NavigationResults.clear] after handling it.
     */
    fun <T : Any> navigateBackWithResult(
        key: String,
        result: T,
        destination: AppRoute? = null,
    )

    /**
     * Navigates up (pops one entry) if possible.
     * 
     * No-op when the stack has a single root entry.
     */
    fun navigateUp()

    /**
     * Binds this navigator to a Navigation 3 back stack.
     * 
     * @param backStack the mutable list used by NavDisplay
     * @param results the result store where back results are published
     * 
     * Call this once (e.g. in AppRoot's LaunchedEffect) and keep it alive
     * while the UI is active. The navigator will apply queued navigation
     * commands to the given [backStack].
     */
    suspend fun bind(
        backStack: MutableList<NavKey>,
        results: NavigationResults,
    )
}
